import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, Star, Clock, Users, Smile, Gift, CheckCircle } from 'lucide-react';

const heroImage = 'https://customer-assets.emergentagent.com/job_9ccab7eb-9b72-427d-bdcd-0c1931e52daf/artifacts/z8qopmuq_IMG-20251124-WA0000.jpg';

const HomeImproved = () => {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionsData, productsData] = await Promise.all([
          api.getCollections(),
          api.getProducts()
        ]);
        setCollections(collectionsData);
        setProducts(productsData.slice(0, 4));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - Emotional & Benefit-Focused */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Capture Life's Special Moments
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100">
            Send personalized, edible postcards that make every occasion unforgettable
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/edible-greetings/catalog">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg">
                Browse Cards
              </Button>
            </Link>
            <Link to="/edible-greetings/product/692429f8d8171bf26874ea4a">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg">
                Design Your Own
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Edible Greetings?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              More than just a card - it's an edible experience they'll never forget
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Truly Personal</h3>
              <p className="text-gray-600">
                Customize every detail with our designer tool. Add your own text, photos, and messages.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">100% Edible</h3>
              <p className="text-gray-600">
                Made with premium edible paper and food-safe ink. Delicious and safe to eat!
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Quick & Easy</h3>
              <p className="text-gray-600">
                Design in minutes, order with a click. Perfect for last-minute celebrations!
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Three simple steps to create memorable moments</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Choose or Design</h3>
              <p className="text-gray-600">
                Browse our collection or create your own custom design with our easy-to-use designer tool
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-green-500 to-green-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Personalize</h3>
              <p className="text-gray-600">
                Add your message, choose colors and fonts, make it uniquely yours
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Receive & Enjoy</h3>
              <p className="text-gray-600">
                We print and ship your edible postcard. Your recipient gets a delicious surprise!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gray-50">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Popular Cards</h2>
            <p className="text-gray-600">Customer favorites for every occasion</p>
          </div>
          <Link to="/edible-greetings/catalog" className="text-gray-900 hover:text-gray-600 transition-colors font-medium">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <Link key={product._id} to={`/product/${product._id}`}>
              <Card className="group overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-base font-medium text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                  <p className="text-gray-900 font-bold text-lg">${product.price.toFixed(2)}</p>
                  {product.customizable && (
                    <span className="inline-block mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      ✨ Customizable
                    </span>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Shop by Collection Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-4xl font-bold text-gray-900 mb-8">Shop by Occasion</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {collections.map((collection) => (
            <Link key={collection._id} to={`/catalog?collection=${collection.slug}`}>
              <div className="relative group overflow-hidden rounded-xl aspect-video shadow-lg hover:shadow-2xl transition-all duration-300">
                <img
                  src={collection.image}
                  alt={collection.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{collection.name}</h3>
                  <p className="text-gray-200">{collection.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Testimonials / Social Proof Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Loved by Thousands</h2>
            <p className="text-xl text-gray-600">See what our customers are saying</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The edible postcard was a huge hit at my daughter's birthday! Everyone loved the unique idea and the taste was amazing!"
              </p>
              <p className="font-medium text-gray-900">- Sarah M.</p>
            </Card>

            <Card className="p-6">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Perfect for our corporate event! The custom business cards were a conversation starter. High quality and great service."
              </p>
              <p className="font-medium text-gray-900">- John D.</p>
            </Card>

            <Card className="p-6">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 italic">
                "I was skeptical at first, but the design tool was so easy to use! My anniversary card turned out perfect and my wife loved it!"
              </p>
              <p className="font-medium text-gray-900">- Michael R.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Create Something Special?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Start designing your personalized edible postcard today
          </p>
          <Link to="/edible-greetings/catalog">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-12 py-6 text-xl font-semibold">
              Get Started Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomeImproved;
